import * as Sentry from '@sentry/react';
import { call, put, select } from 'redux-saga/effects';
import {
  getAll,
  getApprovedFiles,
  getArchived,
  getTemplates,
} from 'api/cloudsave/storageReads';
import { errorMessage } from 'containers/Snackbar/snackbarMessages';
import { getIsOnline } from 'store/connection/selectors';
import { withRetry } from 'store/sagaUtils';
import { addMessage } from 'store/snackbar/reducer';
import { storageUnitCache } from 'util/cache/storageUnitCacheIndexedDB';
import {
  setApprovedFiles,
  setArchived,
  setExplorerLoading,
  setMoreDataToRetrieve,
  setRecentFiles,
  setTemplates,
} from './reducer';
import type {
  GetApprovedFilesRequestActionInterface,
  GetArchivedRequestActionInterface,
  GetTemplatesRequestActionInterface,
} from './sagaActions';
import { StorageUnitType } from './types';
import type { StorageUnit } from './types';
import { ApiStorageUnitFilter } from 'api/cloudsave/cloudsaveApi';

export function* fetchRecentCollection() {
  try {
    const isOnline: boolean = yield select(getIsOnline);
    if (!isOnline) {
      return;
    }

    const { parsedBody } = yield withRetry(() =>
      getAll({
        limit: 10,
        offset: 0,
        filter: ApiStorageUnitFilter.RECENT,
        type: StorageUnitType.File,
      }),
    );
    yield put(setRecentFiles(parsedBody ?? []));
  } catch (error) {
    Sentry.captureException(error, {
      tags: { saga: 'getRecentFiles' },
    });
    yield put(
      addMessage(
        errorMessage("Couldn't fetch recent files. Please try again later."),
      ),
    );
  }
}

export function* fetchTemplatesCollection(
  action: GetTemplatesRequestActionInterface,
) {
  try {
    const { limit, offset, replace, searchString } = action.payload;
    yield put(setExplorerLoading(true));

    const isOnline: boolean = yield select(getIsOnline);
    if (!isOnline) {
      yield put(setMoreDataToRetrieve(false));
      return;
    }

    const { parsedBody } = yield withRetry(() =>
      getTemplates(limit, offset, searchString),
    );

    yield put(setTemplates({ templates: parsedBody, replace: !!replace }));
    yield put(setMoreDataToRetrieve(parsedBody.length === limit));
  } catch (error) {
    Sentry.captureException(error, {
      tags: { saga: 'getTemplates' },
      extra: action.payload,
    });
    yield put(
      addMessage(
        errorMessage("Couldn't fetch templates. Please try again later."),
      ),
    );
  } finally {
    yield put(setExplorerLoading(false));
  }
}

export function* fetchArchivedCollection(
  action: GetArchivedRequestActionInterface,
) {
  try {
    const { limit, offset, replace } = action.payload;
    yield put(setExplorerLoading(true));

    const isOnline: boolean = yield select(getIsOnline);
    if (!isOnline) {
      const units: StorageUnit[] = yield call([
        storageUnitCache,
        storageUnitCache.getArchived,
      ]);
      yield put(setArchived({ units, replace: !!replace }));
      yield put(setMoreDataToRetrieve(false));
      return;
    }

    const { parsedBody } = yield withRetry(() => getArchived(limit, offset));
    const units = (parsedBody as StorageUnit[]).map((unit) => ({
      ...unit,
      archived: true,
    }));
    yield put(setArchived({ units, replace: !!replace }));
    yield put(setMoreDataToRetrieve(parsedBody.length === limit));
  } catch (error) {
    Sentry.captureException(error, {
      tags: { saga: 'getArchivedUnits' },
      extra: action.payload,
    });
    yield put(addMessage(errorMessage('Failed to fetch archived files')));
  } finally {
    yield put(setExplorerLoading(false));
  }
}

export function* fetchApprovedCollection(
  action: GetApprovedFilesRequestActionInterface,
) {
  try {
    const { limit, offset, replace, searchString } = action.payload;
    yield put(setExplorerLoading(true));

    const isOnline: boolean = yield select(getIsOnline);
    if (!isOnline) {
      yield put(setMoreDataToRetrieve(false));
      return;
    }

    const { parsedBody } = yield withRetry(() =>
      getApprovedFiles(limit, offset, searchString),
    );

    yield put(setApprovedFiles({ files: parsedBody, replace: !!replace }));
    yield put(setMoreDataToRetrieve(parsedBody.length === limit));
  } catch (error) {
    Sentry.captureException(error, {
      tags: { saga: 'getApprovedFiles' },
      extra: action.payload,
    });
    yield put(
      addMessage(
        errorMessage("Couldn't fetch approved files. Please try again later."),
      ),
    );
  } finally {
    yield put(setExplorerLoading(false));
  }
}
